import { inject, Injectable } from '@angular/core';
import {
  concatMap,
  filter,
  map, skip,
  tap,
} from 'rxjs/operators';
import { UserService } from '../../../services/user/user.service';
import { BlogService } from '../../../services/blog.service';
import { BehaviorSubject } from 'rxjs';
import { getRandomNumber } from '../../../helpers/utils';
import { CookieService, PlatformService } from 'ngx-unificator/services';
import { TranslationService } from '../../../services/translation/translation.service';
import { CustomNotificationChanelEnum } from '../../../enums/notifications-enum';
import {
  CustomBlogNotifyType,
  NotificationType,
} from '../../../types/notifications-type';
import { InstallAppService } from '../../../services/install-app.service';
import { LanguageService } from '../../../services/language.service';

@Injectable({
  providedIn: 'root',
})
export class CustomNotificationCenterService {

  private _user = inject(UserService);
  private _blog = inject(BlogService);
  private _cookie = inject(CookieService);
  private _installApp = inject(InstallAppService);
  private _translate = inject(TranslationService);
  private _lang = inject(LanguageService);
  private _platform = inject(PlatformService);

  /**
   * Use for blog custom chanel
   * @private
   */
  private _blogCustomNotificationChannel$: BehaviorSubject<NotificationType[]> = new BehaviorSubject<NotificationType[]>([]);

  /**
   * Use for install app custom chanel
   * @private
   */
  private _installAppChannel$: BehaviorSubject<NotificationType> = new BehaviorSubject<NotificationType>(null);

  public notificationSubjectList$ = [
    this._blogCustomNotificationChannel$,
    this._installAppChannel$,
  ];

  public initCustomNotify() {
    if (this._platform.isBrowser) {
      this._initBlogCustomNotificationMessage();
      this._initInstallForNotification();
    }
  }

  private _initInstallForNotification() {
    if (this._installApp.showInstallAppNotificationMessage &&
      !this._installApp.isNativeApp &&
      !this._installApp.hideInstallAppByCMS) {
      this._resolveInstallAppChannel();
    }
  }

  private _initBlogCustomNotificationMessage() {
    this._user.auth$.pipe(
      filter(auth => !!auth),
      concatMap(() => this._lang.langChange$),
      concatMap(() => this._blog.list({
        type: 8,
      })),
      filter(notification => !!notification),
      map((data: CustomBlogNotifyType[]) => {
        return data?.filter((article: CustomBlogNotifyType) => !this._cookie.check(article.slug));
      }),
      tap((list: CustomBlogNotifyType[]) => this._resolveBlogNotification(list)),
    ).subscribe();
  }

  /**
   * Resolve fields for install app message
   * @private
   */
  private _resolveInstallAppChannel() {
    this._installAppChannel$.next({
      uid: getRandomNumber(111, 111111111111111),
      notify_channel: CustomNotificationChanelEnum.INSTALL_APP,
      link: '/mobile-app',
      title: this._translate.translate('t.all-games-bonuses-app'),
      description: this._translate.translate('link.install-app'),
      img: '/assets/img/mobile-app/icon.png',
    });
  };

  private _resolveBlogNotification(list: CustomBlogNotifyType[]) {

    const blogs: NotificationType[] = list.map((item: CustomBlogNotifyType) => (
      {
        uid: getRandomNumber(111, 111111111111111),
        notify_channel: CustomNotificationChanelEnum.BLOG_NOTIFICATIONS,
        link: item.Link,
        title: item.Title,
        description: item.Description,
        img: item.Image,
        slug: item.slug,
        IsPinned: Boolean(item.IsPinned),
      }
    ));

    this._blogCustomNotificationChannel$.next([...blogs]);
  }
}
